import React, { useState, useEffect } from 'react';
import {
  Drawer, Form, Input, Button, Space, Upload, message,
  Typography, Spin, Divider,
} from 'antd';
import { SaveOutlined, UploadOutlined } from '@ant-design/icons';
import RichTextEditor from '../../components/RichTextEditor';
import { condicionesService, API_HOST } from '../../services/api';

const { Title, Text } = Typography;

// Configuración de cada sección
const SECCIONES = [
  {
    key:   'comercial',
    label: 'Condiciones Comerciales',
    titulo: 'titulo_comercial',
    sub:    'subtitulo_comercial',
    cuerpo: 'condiciones_comerciales',
  },
  {
    key:   'sanitaria',
    label: 'Condiciones Sanitarias',
    titulo: 'titulo_sanitaria',
    sub:    'subtitulo_sanitaria',
    cuerpo: 'condiciones_sanitarias',
  },
  {
    key:   'reclamos',
    label: 'Reclamos y Garantías',
    titulo: 'titulo_reclamos',
    sub:    'subtitulo_reclamos',
    cuerpo: 'reclamos_garantias',
  },
  {
    key:   'garantia',
    label: 'Garantía',
    titulo: 'titulo_garantia',
    sub:    null,
    cuerpo: null,   // solo PDF
  },
];

const CondicionesDrawer = ({ eventoId, seccion, onClose }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfActual, setPdfActual] = useState(null);
  const [richContent, setRichContent] = useState('');

  const cfg = SECCIONES.find(s => s.key === seccion);

  useEffect(() => {
    if (!eventoId || !seccion) return;
    setLoading(true);
    condicionesService.getByEvento(eventoId)
      .then(res => {
        const d = res.data || {};
        form.setFieldsValue({
          titulo:    d[cfg.titulo]  || '',
          subtitulo: cfg.sub ? d[cfg.sub] || '' : undefined,
        });
        if (cfg.cuerpo) setRichContent(d[cfg.cuerpo] || '');
        if (cfg.key === 'garantia') setPdfActual(d.garantia || null);
      })
      .catch(() => message.error('Error al cargar condiciones'))
      .finally(() => setLoading(false));
    // cfg se deriva de seccion via SECCIONES.find() (constante de módulo, referencia estable).
    // form es ref estable de antd Form.useForm().
  }, [eventoId, seccion, cfg, form]);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      let payload;

      if (pdfFile) {
        payload = new FormData();
        payload.append('garantia_pdf', pdfFile);
        payload.append(cfg.titulo, values.titulo || '');
      } else {
        payload = { [cfg.titulo]: values.titulo || '' };
        if (cfg.sub)    payload[cfg.sub]    = values.subtitulo || '';
        if (cfg.cuerpo) payload[cfg.cuerpo] = richContent || '';
      }

      await condicionesService.update(eventoId, payload);
      message.success('Guardado correctamente');
      onClose();
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;

  return (
    <Drawer
      title={<Title level={4} style={{ margin: 0 }}>{cfg.label}</Title>}
      open={!!seccion}
      onClose={onClose}
      width={680}
      footer={
        <Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={() => form.submit()}
          >
            Guardar
          </Button>
          <Button onClick={onClose}>Cancelar</Button>
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : (
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="titulo" label="Título">
            <Input placeholder={`Ej: ${cfg.label}`} maxLength={255} showCount />
          </Form.Item>

          {cfg.sub && (
            <Form.Item name="subtitulo" label="Subtítulo">
              <Input.TextArea rows={2} placeholder="Breve descripción o bajada" maxLength={500} showCount />
            </Form.Item>
          )}

          {cfg.cuerpo && (
            <Form.Item label="Contenido">
              <RichTextEditor
                value={richContent}
                onChange={setRichContent}
              />
            </Form.Item>
          )}

          {cfg.key === 'garantia' && (
            <>
              <Divider />
              <Form.Item label="Archivo PDF de garantía">
                {pdfActual && (
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>PDF actual: </Text>
                    <a href={`${API_HOST}${pdfActual}`} target="_blank" rel="noopener noreferrer">
                      Ver PDF
                    </a>
                  </div>
                )}
                <Upload
                  accept=".pdf"
                  beforeUpload={(file) => { setPdfFile(file); return false; }}
                  maxCount={1}
                  fileList={pdfFile ? [{ uid: '1', name: pdfFile.name, status: 'done' }] : []}
                  onRemove={() => setPdfFile(null)}
                >
                  <Button icon={<UploadOutlined />}>Subir PDF</Button>
                </Upload>
              </Form.Item>
            </>
          )}
        </Form>
      )}
    </Drawer>
  );
};

export default CondicionesDrawer;
